/**
 * Dokumente am Partner: Crefo-Auskunft, Vollmachten, Verträge.
 *
 * Fest hinterlegte Schriftstücke, die zum Partner gehören statt zu einem
 * einzelnen Beleg. Ein Gültig-bis-Datum macht ablaufende Unterlagen
 * sichtbar — eine abgelaufene Vollmacht erscheint rot, bevor sich jemand
 * darauf verlässt.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  PARTNER_ATTACHMENT_CATEGORIES,
  PARTNER_ATTACHMENT_CATEGORY_LABELS,
  formatDate,
  formatTimestamp,
  type PartnerAttachmentCategory,
} from '@erp/shared';

import { api, ApiError, apiUrl } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from './toast';
import { Badge, CollapsibleTitle, EmptyState, TextInput, useCollapsed } from './ui';

type PartnerAttachment = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  category: PartnerAttachmentCategory;
  note: string | null;
  validUntil: string | null;
  createdAt: string;
};

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function validityBadge(attachment: PartnerAttachment) {
  if (!attachment.validUntil) return <span className="faint">unbefristet</span>;
  const today = new Date().toISOString().slice(0, 10);
  if (attachment.validUntil < today) {
    return <Badge kind="danger">abgelaufen {formatDate(attachment.validUntil)}</Badge>;
  }
  return <>bis {formatDate(attachment.validUntil)}</>;
}

export function PartnerDocuments({ partnerId }: { partnerId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('masterdata:write');

  const inputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<PartnerAttachmentCategory>('other');
  const [validUntil, setValidUntil] = useState('');
  const [collapsed, toggle] = useCollapsed('partner-dokumente');

  const { data, isLoading } = useQuery({
    queryKey: ['partner-attachments', partnerId],
    queryFn: () =>
      api.get<{ attachments: PartnerAttachment[] }>(`/api/partners/${partnerId}/attachments`),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('category', category);
      if (validUntil) form.append('validUntil', validUntil);
      form.append('file', file, file.name);

      // FormData nicht durch den JSON-Client - der Browser setzt die
      // Multipart-Grenze selbst.
      const response = await fetch(apiUrl(`/api/partners/${partnerId}/attachments`), {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(
          response.status,
          'UPLOAD',
          payload.message ?? 'Die Datei konnte nicht abgelegt werden.',
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-attachments', partnerId] });
      toast.success('Das Dokument wurde hinterlegt.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Upload ist fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/partner-attachments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-attachments', partnerId] });
      toast.success('Das Dokument wurde entfernt.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Entfernen fehlgeschlagen.'),
  });

  const attachments = data?.attachments ?? [];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: collapsed ? 0 : 10, flexWrap: 'wrap', gap: 8 }}>
          <CollapsibleTitle
            title="Dokumente"
            subtitle="Crefo-Auskunft, Vollmachten, Verträge — fest am Partner hinterlegt"
            collapsed={collapsed}
            onToggle={toggle}
          />
          {mayWrite && !collapsed ? (
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <select
                className="select input"
                style={{ width: 180 }}
                value={category}
                onChange={(event) => setCategory(event.target.value as PartnerAttachmentCategory)}
              >
                {PARTNER_ATTACHMENT_CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {PARTNER_ATTACHMENT_CATEGORY_LABELS[value]}
                  </option>
                ))}
              </select>
              <TextInput
                type="date"
                value={validUntil}
                onChange={setValidUntil}
                placeholder="Gültig bis"
              />
              <button
                type="button"
                className="btn btn-sm"
                disabled={upload.isPending}
                onClick={() => inputRef.current?.click()}
              >
                {upload.isPending ? 'Wird übertragen …' : 'Datei hinterlegen'}
              </button>
              <input
                ref={inputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) upload.mutate(file);
                  event.target.value = '';
                }}
              />
            </div>
          ) : null}
        </div>

        {collapsed || isLoading ? null : attachments.length === 0 ? (
          <EmptyState
            title="Noch keine Dokumente hinterlegt."
            hint={mayWrite ? 'Art wählen, bei befristeten Unterlagen das Gültig-bis-Datum setzen, Datei hinterlegen.' : undefined}
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Datei</th>
                  <th className="shrink">Art</th>
                  <th className="shrink">Gültigkeit</th>
                  <th className="numeric">Größe</th>
                  <th className="shrink">Abgelegt</th>
                  <th className="shrink" />
                </tr>
              </thead>
              <tbody>
                {attachments.map((attachment) => (
                  <tr key={attachment.id}>
                    <td>
                      <a
                        href={apiUrl(`/api/partner-attachments/${attachment.id}/download`)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {attachment.fileName}
                      </a>
                    </td>
                    <td>
                      <Badge kind={attachment.category === 'crefo' ? 'accent' : 'neutral'}>
                        {PARTNER_ATTACHMENT_CATEGORY_LABELS[attachment.category] ?? attachment.category}
                      </Badge>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{validityBadge(attachment)}</td>
                    <td className="numeric muted">{formatSize(attachment.sizeBytes)}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(attachment.createdAt)}
                    </td>
                    <td>
                      {mayWrite ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(attachment.id)}
                        >
                          Entfernen
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
